/**
 * WEB-2058 — ОТКАЗЫ СОХРАНЕНИЯ, вынесены из хранилища редактора отдельным
 * модулем: приметы отказа сервера по тарифу и оплате (WEB-391/657), их
 * классификация для политики автоповтора и общие редьюсеры `.rejected`
 * сохранения/публикации/сборки. Перенесены дословно; хранилище реэкспортирует
 * приметы и классификатор, прежние импорты целы.
 */
import { extractApiError } from '@/api/errorStatus';
import type { EditorState } from '../editorSlice';

/** Sentinel payload returned when the backend responds with HTTP 402 on a
 * build operation (insufficient wallet balance). The ThreePanelEditor catch
 * block checks for this value to show a targeted "top up" message instead of
 * the generic save-failed toast. */
export const PAYMENT_REQUIRED_SENTINEL = 'PAYMENT_REQUIRED';

/** WEB-391: sentinel for a 402 carrying `code: 'TIER_LIMIT'` — a free-tier
 * publish/build limit. Unlike the wallet-balance PAYMENT_REQUIRED case (WEB-136,
 * which intentionally surfaces as a fatal editor error), a tier-limit is a
 * recoverable UPSELL: the editor must stay mounted and show the PricingModal,
 * NOT collapse to a raw error Alert. The rejected reducers therefore skip
 * setting `state.error` for this sentinel. */
export const TIER_LIMIT_SENTINEL = 'TIER_LIMIT';

/** WEB-657: reject payload of the save-draft thunks for non-402 failures.
 * Carries the HTTP status (attached by the api client interceptor; null for
 * network-level failures) so the autosave retry policy can distinguish a
 * server-rejected draft (4xx → stop retrying) from a transient failure
 * (5xx/network → bounded exponential backoff). */
export interface SaveRejection {
  status: number | null;
  message: string;
  /** WEB-1333: код ошибки сервера (например STALE_DRAFT) — для адресных
   * сообщений в редакторе вместо общего «Не удалось сохранить». */
  code?: string;
}

/** WEB-657: classify a save-thunk reject payload for the autosave retry policy.
 * 'stop'  — never auto-retry: both 402 sentinels (billing path) and any other
 *           4xx (validation / rejected content — the same payload can never
 *           succeed on its own).
 * 'retry' — transient (5xx, network without status, unknown shape): retry with
 *           backoff. */
export function classifySaveRejection(payload: unknown): 'stop' | 'retry' {
  if (
    payload === TIER_LIMIT_SENTINEL ||
    payload === PAYMENT_REQUIRED_SENTINEL
  ) {
    return 'stop';
  }
  const status =
    typeof payload === 'object' && payload !== null
      ? (payload as { status?: number | null }).status
      : null;
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return 'stop';
  }
  return 'retry';
}

/**
 * WEB-1955: ОТКАЗ ПУБЛИКАЦИИ — своя дверь, судит по ТХАНКУ, а не по форме
 * пейлоада (разобранный отказ публикации — тоже объект, как у сохранения).
 *
 * Ревизия ранней части пачки 49 (замечание «редактор з1»): обычный отказ
 * публикации (нет домена, негодный блок, не ответили за 30 секунд) —
 * ВОССТАНОВИМЫЙ. Прежде он писался в смертельную `error`, оболочка уходила в
 * экран ошибки, и вернуться к воронке можно было только перезагрузкой, хотя
 * черновик цел (сохранение идёт до публикации). Причину автору называет
 * вызывающий — `shell/publish/usePublishFlow` показывает текст отказа через
 * реестр кодов; он единственный, кто зовёт публикацию, так что отказ не
 * проглочен.
 *
 * Решение владельца 05.10: примета кошелька («нет средств», WEB-136) — так
 * же. Её ловят по ССЫЛКЕ в отказе санка (`usePublishFlow` показывает
 * «пополните баланс», пара ru/en), а не в хранилище, поэтому смертельная
 * `error` ничего не добавляла, кроме экрана ошибки без пути назад. Оплата не
 * обходится: публикацию отклонил сервер. Приметы по-прежнему сравниваются по
 * ссылке, саму строку приметы не трогать.
 */
export function rejectedPublishState(
  state: EditorState,
  _payload: unknown,
): EditorState {
  return { ...state, isSaving: false };
}

/** Shared reducer for save/publish `.rejected`. WEB-391: a TIER_LIMIT_SENTINEL
 * is a recoverable upsell — it must NOT populate the fatal `error` field (which
 * collapses the editor to an Alert). Any other payload is a real failure. */
export function rejectedBuildState(
  state: EditorState,
  payload: unknown,
): EditorState {
  // WEB-2556: примета кошелька на СОХРАНЕНИИ черновика — тоже восстановимый
  // отказ. Смертельная `error` сворачивала редактор в общую ошибку, и
  // сообщение «пополните баланс» (useDraftPersistence) терялось вместе с ним;
  // правки при этом живы (isDirty остаётся). Публикация держит смертельный
  // путь своей дверью — rejectedPublishState выше.
  if (payload === TIER_LIMIT_SENTINEL || payload === PAYMENT_REQUIRED_SENTINEL) {
    return { ...state, isSaving: false };
  }
  // WEB-657: a classified save-draft reject ({status, message}) is recoverable —
  // the SaveIndicator shows «Не сохранено — повторить» and isDirty stays true,
  // so the editor must NOT collapse into the fatal ErrorState.
  //
  // WEB-1955: у публикации теперь СВОЯ дверь (`rejectedPublishState`), потому
  // что различать «восстановимо» и «смертельно» по ФОРМЕ значения больше
  // нельзя — разобранный отказ тоже объект. Сюда доезжает только сохранение
  // черновика и сентинел кошелька.
  if (typeof payload === 'object' && payload !== null) {
    return { ...state, isSaving: false };
  }
  return {
    ...state,
    isSaving: false,
    error: payload == null ? null : extractApiError(payload),
  };
}
